
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

export default function ResourcesPage() {
  const services = [
    {
      icon: 'ri-phone-line',
      title: '24/7 Crisis Hotline',
      description: 'Immediate support available around the clock for survivors and their families.',
      contact: '1-800-HOPE-NOW',
      action: 'Call Now'
    },
    {
      icon: 'ri-mental-health-line',
      title: 'Counseling Services',
      description: 'Trauma-informed therapy with licensed professionals specializing in crisis recovery.',
      contact: 'Individual & Group Sessions',
      action: 'Learn More'
    },
    {
      icon: 'ri-scales-3-line',
      title: 'Legal Advocacy',
      description: 'Free legal consultation and representation for survivors navigating the justice system.',
      contact: 'Pro Bono Services',
      action: 'Get Help'
    },
    {
      icon: 'ri-home-heart-line',
      title: 'Housing Assistance',
      description: 'Emergency shelter and transitional housing support for survivors in need.',
      contact: 'Safe Housing Network',
      action: 'Apply Now'
    },
    {
      icon: 'ri-briefcase-line',
      title: 'Job Training',
      description: 'Vocational training and employment assistance to help survivors rebuild independence.',
      contact: 'Career Development Program',
      action: 'Enroll'
    },
    {
      icon: 'ri-team-line',
      title: 'Support Groups',
      description: 'Peer support groups where survivors can connect and share their experiences.',
      contact: 'Weekly Meetings',
      action: 'Join Us'
    }
  ];

  const resources = [
    {
      title: 'Survivor\'s Guide to Recovery',
      description: 'A comprehensive guide covering the stages of recovery and available resources.',
      type: 'PDF Guide',
      icon: 'ri-file-pdf-line'
    },
    {
      title: 'Family Support Handbook',
      description: 'Resources for family members supporting a loved one through recovery.',
      type: 'PDF Guide',
      icon: 'ri-file-pdf-line'
    },
    {
      title: 'Know Your Rights',
      description: 'Legal rights and protections available to survivors of kidnapping.',
      type: 'PDF Guide',
      icon: 'ri-file-pdf-line'
    },
    {
      title: 'Self-Care Toolkit',
      description: 'Practical tools and techniques for managing trauma and stress.',
      type: 'PDF Guide',
      icon: 'ri-file-pdf-line'
    }
  ];

  const faqs = [
    {
      question: 'How do I access your services?',
      answer: 'You can reach us through our 24/7 crisis hotline, contact form, or by visiting our office. All services are confidential and free of charge.'
    },
    {
      question: 'Are your services confidential?',
      answer: 'Yes, all our services are completely confidential. We prioritize your privacy and safety above all else.'
    },
    {
      question: 'Do I need to report to police to receive help?',
      answer: 'No, you do not need to report to police to access our services. We support survivors regardless of their decision to involve law enforcement.'
    },
    {
      question: 'Can family members access support?',
      answer: 'Yes, we offer support services for family members and loved ones of survivors, including counseling and support groups.'
    },
    {
      question: 'How long does the recovery process take?',
      answer: 'Recovery is different for everyone. We provide ongoing support for as long as you need it, with no time limits on our services.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative pt-20 sm:pt-24">
        <div className="bg-gradient-to-r from-[#6a4bc3] to-[#8a6bd3] py-16 sm:py-24 px-4 sm:px-6 lg:px-20">
          <div className="max-w-7xl mx-auto">
            <div className="max-w-3xl">
              <p className="text-white/80 text-sm mb-3">GET HELP</p>
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 sm:mb-6">
                Resources & Support
              </h1>
              <p className="text-white/90 text-base sm:text-lg mb-8">
                We provide comprehensive support services for survivors and their families. All services are free, confidential, and available to anyone in need.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a
                  href="tel:1-800-HOPE-NOW"
                  className="bg-[#c9a24d] text-white px-8 py-3 rounded-full font-semibold hover:bg-[#b8923d] transition-colors cursor-pointer whitespace-nowrap inline-flex items-center justify-center gap-2"
                >
                  <i className="ri-phone-line"></i>
                  Call Crisis Hotline
                </a>
                <Link
                  to="/contact"
                  className="bg-white/20 text-white px-8 py-3 rounded-full font-semibold hover:bg-white/30 transition-colors cursor-pointer whitespace-nowrap text-center"
                >
                  Contact Us
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Emergency Banner */}
      <section className="bg-red-50 border-y border-red-200 py-4 px-4 sm:px-6 lg:px-20">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-center gap-3 text-center sm:text-left">
          <div className="flex items-center gap-2 text-red-700">
            <i className="ri-alarm-warning-line text-xl"></i>
            <span className="font-semibold">In immediate danger?</span>
          </div>
          <span className="text-red-600">Call 911 or our 24/7 Crisis Hotline:</span>
          <a href="tel:1-800-HOPE-NOW" className="text-red-700 font-bold hover:underline cursor-pointer">
            1-800-HOPE-NOW
          </a>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-12 sm:py-20 px-4 sm:px-6 lg:px-20">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10 sm:mb-12">
            <p className="text-[#c9a24d] text-sm font-semibold mb-3">OUR SERVICES</p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              How We Can Help
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Our comprehensive services address every aspect of recovery, from immediate crisis support to long-term healing.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl p-6 sm:p-8 shadow-sm hover:shadow-lg transition-all border border-gray-100"
              >
                <div className="w-14 h-14 bg-[#6a4bc3]/10 rounded-full flex items-center justify-center mb-4">
                  <i className={`${service.icon} text-2xl text-[#6a4bc3]`}></i>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{service.title}</h3>
                <p className="text-gray-600 text-sm mb-4">{service.description}</p>
                <p className="text-[#c9a24d] text-sm font-medium mb-4">{service.contact}</p>
                <Link
                  to="/contact"
                  className="text-[#6a4bc3] font-semibold text-sm flex items-center gap-2 hover:gap-3 transition-all cursor-pointer"
                >
                  {service.action}
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Downloadable Resources */}
      <section className="py-12 sm:py-16 px-4 sm:px-6 lg:px-20 bg-[#f8f6fc]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10">
            <p className="text-[#c9a24d] text-sm font-semibold mb-3">RESOURCES</p>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
              Helpful Materials
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            {resources.map((resource, index) => (
              <div
                key={index}
                className="bg-white rounded-xl p-5 sm:p-6 hover:shadow-md transition-shadow cursor-pointer group"
              >
                <div className="w-12 h-12 bg-[#6a4bc3]/10 rounded-lg flex items-center justify-center mb-4 group-hover:bg-[#6a4bc3] transition-colors">
                  <i className={`${resource.icon} text-xl text-[#6a4bc3] group-hover:text-white transition-colors`}></i>
                </div>
                <h3 className="font-semibold text-gray-900 mb-2 text-sm">{resource.title}</h3>
                <p className="text-gray-600 text-xs mb-3">{resource.description}</p>
                <span className="text-[#c9a24d] text-xs font-medium">{resource.type}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-12 sm:py-20 px-4 sm:px-6 lg:px-20">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-10">
            <p className="text-[#c9a24d] text-sm font-semibold mb-3">FAQ</p>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
              Frequently Asked Questions
            </h2>
          </div>
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-white rounded-xl p-5 sm:p-6 shadow-sm border border-gray-100">
                <h3 className="font-semibold text-gray-900 mb-2 flex items-start gap-3">
                  <span className="text-[#c9a24d]">Q:</span>
                  {faq.question}
                </h3>
                <p className="text-gray-600 text-sm pl-6">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 sm:py-16 px-4 sm:px-6 lg:px-20 bg-[#6a4bc3]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">
            Need Help? We're Here For You
          </h2>
          <p className="text-white/90 mb-8">
            Don't hesitate to reach out. Our team is available 24/7 to provide support and guidance.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="tel:1-800-HOPE-NOW"
              className="bg-white text-[#6a4bc3] px-8 py-3 rounded-full font-semibold hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap inline-flex items-center justify-center gap-2"
            >
              <i className="ri-phone-line"></i>
              1-800-HOPE-NOW
            </a>
            <Link
              to="/contact"
              className="bg-[#c9a24d] text-white px-8 py-3 rounded-full font-semibold hover:bg-[#b8923d] transition-colors cursor-pointer whitespace-nowrap"
            >
              Contact Us Online
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
